import { useState, useEffect, useCallback } from 'react';
import { useSSE } from '../app/providers/SseProvider';
import { pdfGeneratorClient } from '@terra/shared-rest-clients-pdf-generator';
import { PDF_GENERATION_PROGRESS_SSE_EVENT } from '@terra/shared-contracts-pdf-generator';
import { getCurrentGatewayUrl } from '../utils/environmentDetection';
import { getAuthHeaders } from '../utils/authHeaders';
import type { PdfTemplate as PdfTemplateSchema } from '@terra/shared-schemas-orchestrator';

export type PdfJobStatus =
  | 'PENDING'
  | 'PREPARING'
  | 'FETCHING'
  | 'RENDERING'
  | 'UPLOADING'
  | 'COMPLETED'
  | 'FAILED';

export type PdfJob = {
  id: string;
  status: PdfJobStatus;
  progress: number;
  message: string;
  downloadUrl?: string;
  error?: string;
};

// Re-export the schema type for external use
export type PdfTemplate = PdfTemplateSchema;

type GeneratePdfOptions = {
  reportId: string;
  templateId: string;
  findingIds: string[];
  options?: {
    includeScreenshots?: boolean;
    includeRemediation?: boolean;
    includeFindingsHighLevelOverview?: boolean;
    passwordProtect?: boolean;
    password?: string;
  };
};

type UsePdfGenerationReturn = {
  templates: PdfTemplate[];
  isLoadingTemplates: boolean;
  currentJob: PdfJob | null;
  isGenerating: boolean;
  generatePdf: (options: GeneratePdfOptions) => Promise<string | null>;
  resetJob: () => void;
  fetchTemplates: () => Promise<void>;
};

const SERVICE_NAME = 'pdf-generator';

async function createApiHeaders(): Promise<Record<string, string>> {
  const authHeaders = await getAuthHeaders();

  return authHeaders;
}

function getBaseUrl(): string {
  const gatewayUrl = getCurrentGatewayUrl();

  return `${gatewayUrl}/${SERVICE_NAME}`;
}

export function usePdfGeneration(): UsePdfGenerationReturn {
  const { subscribe } = useSSE();
  const [templates, setTemplates] = useState<PdfTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [currentJob, setCurrentJob] = useState<PdfJob | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setIsLoadingTemplates(true);
      const headers = await createApiHeaders();

      const response = await pdfGeneratorClient.listTemplates({
        query: { includeDefault: true },
        extraHeaders: headers,
        overrideClientOptions: {
          baseUrl: getBaseUrl(),
        },
      });

      if (response.status === 200) {
        setTemplates(response.body.templates);
      } else {
        console.error('Failed to fetch PDF templates:', response);
      }
    } catch (error) {
      console.error('Failed to fetch PDF templates:', error);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, []);

  const generatePdf = useCallback(
    async (options: GeneratePdfOptions): Promise<string | null> => {
      try {
        setIsGenerating(true);
        setCurrentJob({
          id: '',
          status: 'PREPARING',
          progress: 0,
          message: 'Preparing PDF data...',
        });

        const headers = await createApiHeaders();

        setCurrentJob({
          id: '',
          status: 'PREPARING',
          progress: 5,
          message: 'Starting PDF generation...',
        });

        const response = await pdfGeneratorClient.generatePdf({
          body: {
            reportId: options.reportId,
            templateId: options.templateId,
            findingIds: options.findingIds,
            options: options.options,
          },
          extraHeaders: headers,
          overrideClientOptions: {
            baseUrl: getBaseUrl(),
          },
        });

        if (response.status === 202) {
          setCurrentJob({
            id: response.body.jobId,
            status: response.body.status,
            progress: 0,
            message: response.body.message,
          });

          return response.body.jobId;
        } else {
          throw new Error('Failed to start PDF generation');
        }
      } catch (error) {
        console.error('Failed to start PDF generation:', error);
        setCurrentJob({
          id: '',
          status: 'FAILED',
          progress: 0,
          message: 'Failed to start PDF generation',
          error: error instanceof Error ? error.message : 'Unknown error',
        });
        setIsGenerating(false);

        return null;
      }
    },
    [],
  );

  const resetJob = useCallback(() => {
    setCurrentJob(null);
    setIsGenerating(false);
  }, []);

  // Subscribe to SSE updates for the current job
  useEffect(() => {
    if (!currentJob?.id) {
      return;
    }

    const unsubscribe = subscribe(
      PDF_GENERATION_PROGRESS_SSE_EVENT,
      (sseData) => {
        if (sseData.jobId !== currentJob.id) return;

        setCurrentJob((prev) => ({
          ...prev,
          id: sseData.jobId,
          status: sseData.status,
          progress: sseData.progress,
          message: sseData.message,
          downloadUrl: sseData.downloadUrl,
          error: sseData.error,
        }));

        if (sseData.status === 'COMPLETED' || sseData.status === 'FAILED') {
          setIsGenerating(false);
        }
      },
    );

    return () => {
      unsubscribe();
    };
  }, [currentJob?.id, subscribe]);

  return {
    templates,
    isLoadingTemplates,
    currentJob,
    isGenerating,
    generatePdf,
    resetJob,
    fetchTemplates,
  };
}
